import React, { type ComponentProps } from 'react';
import { StyleSheet, View } from 'react-native';
import { useA11ySelector } from '@sg/a11y-helpers';
import { LabelText, useInaccessibleElement } from '@sg/garnish';
import { MapWithControls as MapWithControlsRadar } from '@sg/radar-fe';

import { useLocalizationContext } from '@order/Localization';
import { getEnvVars } from '@order/utils';

import { LoadingAnimation } from '../LoadingAnimation';
import { LocationsMapSearchArea } from './LocationsMapSearchArea';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationsMap = (props: MapViewProps) => {
  const {
    isLoadingMap,
    isLoadingMapLocations,
    isSearchAreaBtnEnabled,
    onSearchAreaPress,
    onGeolocateUserBtnPress,
    ...mapProps
  } = props;

  const a11ySelector = useA11ySelector('locations.map.container');
  const { t } = useLocalizationContext();

  // The map should be not be accessible to screen readers -
  // an invisible LabelText is provided instead.
  const mapAltText = t('location.map-a11y-alt-text');
  const mapA11yProps = useInaccessibleElement(true);

  return (
    <>
      <LabelText
        testID="map-a11y-alt-text"
        style={styles.mapAltText}
        accessible={true}
        accessibilityRole="text"
        accessibilityLabel={mapAltText}
        accessibilityHint={mapAltText}
      >
        {mapAltText}
      </LabelText>

      <View
        testID="map-view"
        style={styles.mapViewContainer}
        {...a11ySelector.props}
        {...mapA11yProps}
      >
        {onSearchAreaPress ? (
          <LocationsMapSearchArea
            isLoading={isLoadingMapLocations}
            disabled={!isSearchAreaBtnEnabled}
            onPress={onSearchAreaPress}
          />
        ) : null}

        {isLoadingMap ? <LoadingAnimation /> : null}

        {isLoadingMap ? null : (
          <MapWithControlsRadar
            {...mapProps}
            publishableKey={RADAR_SDK_KEY}
            disableGeolocateUserBtn={isLoadingMapLocations}
            onGeolocateUserBtnPress={onGeolocateUserBtnPress}
          />
        )}
      </View>
    </>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  mapViewContainer: {
    flex: 1,
  },

  // hide map alt text label from users but leave it visible to screen readers
  mapAltText: {
    position: 'absolute',
    overflow: 'hidden',
    width: 1,
    height: 1,
    margin: -1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MapViewProps = Readonly<{
  isLoadingMap?: boolean;
  isLoadingMapLocations?: boolean;
  isSearchAreaBtnEnabled?: boolean;
  onSearchAreaPress?: () => void;
}> &
  Omit<ComponentProps<typeof MapWithControlsRadar>, 'publishableKey'>;
